import { useMemo, useState } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button.tsx';
import { SearchInput } from '@/components/search-input.tsx';
import { CredenzaFacetedFilter } from '@/components/credenza-faceted-filter.tsx';
import { TUserWithAccessControl } from '@/features/users/schemas/users.types.ts';
import { buildFacetOptions } from '@/utils/utils.ts';

// eslint-disable-next-line react-refresh/only-export-components
export function useRuleAssignmentDesktopFilters(users: TUserWithAccessControl[]) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());

  const roleOptions = useMemo(() => buildFacetOptions(users, (u) => u.role), [users]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        searchQuery === '' ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = selectedRoles.size === 0 || selectedRoles.has(user.role);
      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, selectedRoles]);

  const isFiltered = searchQuery !== '' || selectedRoles.size > 0;

  return {
    searchQuery,
    setSearchQuery,
    selectedRoles,
    setSelectedRoles,
    roleOptions,
    filteredUsers,
    isFiltered,
  };
}

export type RuleAssignmentDesktopFilters = ReturnType<typeof useRuleAssignmentDesktopFilters>;

export function RuleAssignmentDesktopToolbar({
  filters,
}: {
  filters: RuleAssignmentDesktopFilters;
}) {
  const {
    searchQuery,
    setSearchQuery,
    roleOptions,
    selectedRoles,
    setSelectedRoles,
    isFiltered,
  } = filters;

  return (
    <div className="flex flex-wrap gap-2">
      <SearchInput
        value={searchQuery}
        onValueChange={setSearchQuery}
        placeholder="Filter users..."
        className="w-full sm:max-w-[300px]"
      />
      <CredenzaFacetedFilter
        title="Role"
        options={roleOptions}
        selectedValues={selectedRoles}
        onSelectedValuesChange={setSelectedRoles}
      />
      {isFiltered && (
        <Button
          variant="ghost"
          onClick={() => {
            setSearchQuery('');
            setSelectedRoles(new Set());
          }}
          className="h-8 w-min px-2 lg:px-3"
        >
          Clear
          <Cross2Icon className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
